import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../components/ThemeContext';
import { API_BASE } from '../../config';

// ============================================================
// Executive Briefing Generator — B5 Frontend (Issue #205)
// Audience selector, scope selector, time period, generate,
// real-time preview with expand/collapse, template management,
// PDF download, and briefing history.
// ============================================================

// ── Types ─────────────────────────────────────────────────────

interface BriefingSection {
  section_key: string;
  section_title: string;
  content: string;
  data_points: Record<string, unknown>[];
  confidence: 'high' | 'medium' | 'low';
}

interface BriefingDetail {
  briefing_id: number;
  title: string;
  audience: string;
  scope: string;
  scope_id: number | null;
  scope_value: string | null;
  time_period: string;
  template_id: number | null;
  sections: BriefingSection[];
  generated_at: string;
  generated_by: number;
  generated_by_name: string;
  token_usage: { input_tokens: number; output_tokens: number; cost_estimate: string };
  pdf_url: string | null;
}

interface BriefingListItem {
  briefing_id: number;
  title: string;
  audience: string;
  scope: string;
  time_period: string;
  section_count: number;
  generated_at: string;
  generated_by_name: string;
  pdf_url: string | null;
}

interface BriefingListResponse {
  briefings: BriefingListItem[];
  total: number;
  page: number;
  page_size: number;
}

interface TemplateSection {
  section_key: string;
  section_title: string;
  prompt_template: string;
  required: boolean;
  order: number;
}

interface BriefingTemplate {
  template_id: number;
  name: string;
  audience: string;
  description: string;
  sections: TemplateSection[];
  is_default: boolean;
  created_by: number | null;
  created_at: string;
}

type AudienceType = 'c_suite' | 'procurement' | 'compliance' | 'board';
type ScopeType = 'full_portfolio' | 'therapeutic_class' | 'manufacturer';
type TimePeriod = '7d' | '30d' | '90d' | '1y';
type ViewMode = 'generator' | 'preview' | 'history' | 'templates';
type HistoryViewStyle = 'table' | 'card';
type SortOrder = 'desc' | 'asc';

// ── Audience Config ───────────────────────────────────────────

const AUDIENCE_CONFIG: Record<AudienceType, { label: string; description: string; icon: React.ReactNode }> = {
  c_suite: {
    label: 'C-Suite',
    description: 'Strategic overview for executive leadership',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      </svg>
    ),
  },
  procurement: {
    label: 'Procurement',
    description: 'Operational brief for sourcing teams',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
    ),
  },
  compliance: {
    label: 'Compliance',
    description: 'Regulatory compliance analysis',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  board: {
    label: 'Board',
    description: 'Quarterly board report',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M9 21V9" />
      </svg>
    ),
  },
};

const AUDIENCE_LABELS: Record<string, string> = {
  c_suite: 'C-Suite',
  procurement: 'Procurement',
  compliance: 'Compliance',
  board: 'Board',
};

const SCOPE_LABELS: Record<string, string> = {
  full_portfolio: 'Full Portfolio',
  therapeutic_class: 'Therapeutic Class',
  manufacturer: 'Manufacturer',
};

const TIME_PERIOD_LABELS: Record<string, string> = {
  '7d': '7 Days',
  '30d': '30 Days',
  '90d': '90 Days',
  '1y': '1 Year',
};

const SECTION_OPTIONS = [
  { key: 'executive_summary', label: 'Executive Summary', required: true },
  { key: 'risk_overview', label: 'Risk Overview', required: false },
  { key: 'compliance_status', label: 'Compliance Status', required: false },
  { key: 'shortage_intelligence', label: 'Shortage Intelligence', required: false },
  { key: 'geopolitical_risks', label: 'Global Risks', required: false },
  { key: 'mia_analysis', label: 'MIA Analysis', required: false },
  { key: 'cost_impact', label: 'Cost Impact', required: false },
  { key: 'recommendations', label: 'Recommendations', required: true },
];

// ── Progress messages for generation ──────────────────────────

const GENERATION_MESSAGES = [
  'Analyzing portfolio risk posture...',
  'Gathering compliance metrics...',
  'Reviewing shortage intelligence...',
  'Assessing global factors...',
  'Generating executive narrative...',
  'Formatting briefing sections...',
  'Calculating confidence scores...',
  'Finalizing briefing...',
];

// ── Component Props ───────────────────────────────────────────

interface ExecutiveBriefingsPageProps {
  SectionHeader: React.ComponentType<{ children: React.ReactNode; sub?: string }>;
  IconSpinner: React.ComponentType;
  IconAlert: React.ComponentType;
  IconShield: React.ComponentType;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function ExecutiveBriefingsPage({
  SectionHeader,
  IconSpinner,
  IconAlert,
  IconShield,
}: ExecutiveBriefingsPageProps) {
  const { T } = useTheme();

  // ── View state ──────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>('generator');

  // ── Generator form state ────────────────────────────────────
  const [audience, setAudience] = useState<AudienceType>('c_suite');
  const [scope, setScope] = useState<ScopeType>('full_portfolio');
  const [scopeValue, setScopeValue] = useState('');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30d');
  const [selectedSections, setSelectedSections] = useState<Set<string>>(
    new Set(SECTION_OPTIONS.map(s => s.key))
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customTitle, setCustomTitle] = useState('');

  // ── Generation state ────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationMessage, setGenerationMessage] = useState('');
  const [generationError, setGenerationError] = useState<string | null>(null);

  // ── Briefing preview state ──────────────────────────────────
  const [currentBriefing, setCurrentBriefing] = useState<BriefingDetail | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedDataPoints, setExpandedDataPoints] = useState<Set<string>>(new Set());

  // ── Briefing history state ──────────────────────────────────
  const [briefingsList, setBriefingsList] = useState<BriefingListItem[]>([]);
  const [briefingsTotal, setBriefingsTotal] = useState(0);
  const [briefingsPage, setBriefingsPage] = useState(1);
  const [briefingsLoading, setBriefingsLoading] = useState(false);
  const [briefingsError, setBriefingsError] = useState<string | null>(null);
  const [historySearch, setHistorySearch] = useState('');
  const [historyAudienceFilter, setHistoryAudienceFilter] = useState<string>('');

  // ── Templates state ─────────────────────────────────────────
  const [templates, setTemplates] = useState<BriefingTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  // ── Delete confirmation ─────────────────────────────────────
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleteSource, setDeleteSource] = useState<'history' | 'detail'>('history');

  // ── Section regeneration ────────────────────────────────────
  const [regeneratingSection, setRegeneratingSection] = useState<string | null>(null);

  // ── PDF download ────────────────────────────────────────────
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // ── History view enhancements (Issue #206) ─────────────────
  const [historyViewStyle, setHistoryViewStyle] = useState<HistoryViewStyle>('table');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selectedBriefings, setSelectedBriefings] = useState<Set<number>>(new Set());
  const [bulkExporting, setBulkExporting] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);
  const [rerunningId, setRerunningId] = useState<number | null>(null);

  // ── Responsive ──────────────────────────────────────────────
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth <= 1024;

  // Generation progress timer ref
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup progress timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, []);

  // ── Load templates on mount ─────────────────────────────────
  const loadTemplatesCallback = useCallback(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    loadTemplatesCallback();
  }, [loadTemplatesCallback]);

  // ── Load briefing history on history view ───────────────────
  useEffect(() => {
    if (viewMode === 'history') {
      loadBriefings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, briefingsPage, historySearch, historyAudienceFilter, sortOrder]);

  // Clear selected briefings when page changes to avoid stale selection across pages
  useEffect(() => {
    setSelectedBriefings(new Set());
  }, [briefingsPage]);

  // ── API: Load templates ─────────────────────────────────────
  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/metasupply/briefings/templates`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Failed to load templates (${response.status})`);
      const data = await response.json();
      setTemplates(data.templates || []);
    } catch {
      // Templates are optional — silently fail
    } finally {
      setTemplatesLoading(false);
    }
  };

  // ── API: Load briefings list ────────────────────────────────
  const loadBriefings = useCallback(async () => {
    setBriefingsLoading(true);
    setBriefingsError(null);
    try {
      const params = new URLSearchParams({
        page: String(briefingsPage),
        page_size: '25',
        sort_by: 'generated_at',
        sort_order: sortOrder,
      });
      if (historySearch) params.set('search', historySearch);
      if (historyAudienceFilter) params.set('audience', historyAudienceFilter);

      const response = await fetch(`${API_BASE}/api/metasupply/briefings?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Failed to load briefings (${response.status})`);
      const data: BriefingListResponse = await response.json();
      setBriefingsList(data.briefings);
      setBriefingsTotal(data.total);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load briefings';
      setBriefingsError(message);
    } finally {
      setBriefingsLoading(false);
    }
  }, [briefingsPage, historySearch, historyAudienceFilter, sortOrder]);

  // ── API: Generate briefing ──────────────────────────────────
  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenerationError(null);
    setGenerationProgress(0);
    setGenerationMessage(GENERATION_MESSAGES[0]);

    // Start progress animation
    let msgIdx = 0;
    progressTimerRef.current = setInterval(() => {
      msgIdx = Math.min(msgIdx + 1, GENERATION_MESSAGES.length - 1);
      setGenerationMessage(GENERATION_MESSAGES[msgIdx]);
      setGenerationProgress(prev => Math.min(prev + 8 + Math.random() * 5, 90));
    }, 2500);

    try {
      const body: Record<string, unknown> = {
        audience,
        scope,
        time_period: timePeriod,
      };
      if (scope === 'therapeutic_class' && scopeValue) {
        body.scope_value = scopeValue;
      }
      if (scope === 'manufacturer' && scopeValue) {
        body.scope_value = scopeValue;
      }
      if (selectedTemplateId) {
        body.template_id = selectedTemplateId;
      }
      if (customTitle.trim()) {
        body.title = customTitle.trim();
      }
      // Only include custom sections if user unchecked some
      const allSelected = SECTION_OPTIONS.every(s => selectedSections.has(s.key));
      if (!allSelected) {
        body.sections = Array.from(selectedSections);
      }

      const response = await fetch(`${API_BASE}/api/metasupply/briefings/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || `Generation failed (${response.status})`);
      }

      const data: BriefingDetail = await response.json();
      setCurrentBriefing(data);
      // Expand all sections by default
      setExpandedSections(new Set(data.sections.map(s => s.section_key)));
      setExpandedDataPoints(new Set());
      setGenerationProgress(100);
      setGenerationMessage('Briefing generated successfully!');

      // Switch to preview after a brief delay
      setTimeout(() => {
        setViewMode('preview');
        setIsGenerating(false);
      }, 500);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to generate briefing';
      setGenerationError(message);
      setIsGenerating(false);
    } finally {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    }
  };

  // ── API: Load briefing detail ───────────────────────────────
  const loadBriefingDetail = async (briefingId: number) => {
    setBriefingsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/metasupply/briefings/${briefingId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Failed to load briefing (${response.status})`);
      const data: BriefingDetail = await response.json();
      setCurrentBriefing(data);
      setExpandedSections(new Set(data.sections.map(s => s.section_key)));
      setExpandedDataPoints(new Set());
      setViewMode('preview');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load briefing';
      setBriefingsError(message);
    } finally {
      setBriefingsLoading(false);
    }
  };

  // ── API: Delete briefing ────────────────────────────────────
  const handleDeleteBriefing = async (briefingId: number) => {
    try {
      const response = await fetch(`${API_BASE}/api/metasupply/briefings/${briefingId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Delete failed (${response.status})`);
      const wasFromDetail = deleteSource === 'detail';
      setDeleteConfirmId(null);
      setDeleteSource('history');
      if (wasFromDetail) {
        // If deleted from detail view, go back to history
        setCurrentBriefing(null);
        setViewMode('history');
      }
      // Refresh list
      loadBriefings();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete briefing';
      setBriefingsError(message);
    }
  };

  // ── API: Duplicate briefing (re-generate with same params) ──
  const handleDuplicateBriefing = async (briefingId: number) => {
    setDuplicatingId(briefingId);
    try {
      // Load the full briefing detail to get its params
      const detailRes = await fetch(`${API_BASE}/api/metasupply/briefings/${briefingId}`, {
        credentials: 'include',
      });
      if (!detailRes.ok) throw new Error(`Failed to load briefing (${detailRes.status})`);
      const detail: BriefingDetail = await detailRes.json();

      // Pre-fill the generator form with this briefing's settings
      if (['c_suite', 'procurement', 'compliance', 'board'].includes(detail.audience)) {
        setAudience(detail.audience as AudienceType);
      }
      if (['full_portfolio', 'therapeutic_class', 'manufacturer'].includes(detail.scope)) {
        setScope(detail.scope as ScopeType);
      }
      if (detail.scope_value) {
        setScopeValue(detail.scope_value);
      }
      if (['7d', '30d', '90d', '1y'].includes(detail.time_period)) {
        setTimePeriod(detail.time_period as TimePeriod);
      }
      setCustomTitle(`${detail.title} (Copy)`);
      if (detail.template_id) {
        setSelectedTemplateId(detail.template_id);
      }
      // Set sections from the original briefing
      const sectionKeys = new Set(detail.sections.map(s => s.section_key));
      setSelectedSections(sectionKeys);

      setViewMode('generator');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to duplicate briefing';
      setBriefingsError(message);
    } finally {
      setDuplicatingId(null);
    }
  };

  // ── API: Re-run briefing (regenerate with latest data) ──────
  const handleRerunBriefing = async (briefingId: number) => {
    setRerunningId(briefingId);
    try {
      // Load the full briefing detail to get its params
      const detailRes = await fetch(`${API_BASE}/api/metasupply/briefings/${briefingId}`, {
        credentials: 'include',
      });
      if (!detailRes.ok) throw new Error(`Failed to load briefing (${detailRes.status})`);
      const detail: BriefingDetail = await detailRes.json();

      // Re-generate with same parameters
      const body: Record<string, unknown> = {
        audience: detail.audience,
        scope: detail.scope,
        time_period: detail.time_period,
      };
      if (detail.scope_value) body.scope_value = detail.scope_value;
      if (detail.template_id) body.template_id = detail.template_id;
      body.title = `${detail.title} (Re-run)`;
      body.sections = detail.sections.map(s => s.section_key);

      setIsGenerating(true);
      setGenerationError(null);
      setGenerationProgress(0);
      setGenerationMessage(GENERATION_MESSAGES[0]);

      let msgIdx = 0;
      progressTimerRef.current = setInterval(() => {
        msgIdx = Math.min(msgIdx + 1, GENERATION_MESSAGES.length - 1);
        setGenerationMessage(GENERATION_MESSAGES[msgIdx]);
        setGenerationProgress(prev => Math.min(prev + 8 + Math.random() * 5, 90));
      }, 2500);

      setViewMode('generator');

      const response = await fetch(`${API_BASE}/api/metasupply/briefings/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || `Re-run failed (${response.status})`);
      }

      const data: BriefingDetail = await response.json();
      setCurrentBriefing(data);
      setExpandedSections(new Set(data.sections.map(s => s.section_key)));
      setExpandedDataPoints(new Set());
      setGenerationProgress(100);
      setGenerationMessage('Briefing re-generated successfully!');

      setTimeout(() => {
        setViewMode('preview');
        setIsGenerating(false);
      }, 500);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to re-run briefing';
      setGenerationError(message);
      setIsGenerating(false);
    } finally {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      setRerunningId(null);
    }
  };

  // ── Bulk selection helpers ──────────────────────────────────
  const toggleBriefingSelection = (id: number) => {
    setSelectedBriefings(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedBriefings.size === briefingsList.length) {
      setSelectedBriefings(new Set());
    } else {
      setSelectedBriefings(new Set(briefingsList.map(b => b.briefing_id)));
    }
  };

  // ── API: Bulk export as ZIP ─────────────────────────────────
  const handleBulkExport = async () => {
    if (selectedBriefings.size === 0) return;
    setBulkExporting(true);
    try {
      // Download each selected briefing as PDF and bundle into a ZIP
      // Since we may not have a server-side ZIP endpoint, download individually
      const ids = Array.from(selectedBriefings);
      for (const id of ids) {
        const response = await fetch(`${API_BASE}/api/metasupply/briefings/${id}/pdf`, {
          credentials: 'include',
        });
        if (!response.ok) continue;
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        a.download = `briefing-${id}-${timestamp}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      setSelectedBriefings(new Set());
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Bulk export failed';
      setBriefingsError(message);
    } finally {
      setBulkExporting(false);
    }
  };

  // ── API: Regenerate section ─────────────────────────────────
  const handleRegenerateSection = async (sectionKey: string) => {
    if (!currentBriefing) return;
    setRegeneratingSection(sectionKey);
    try {
      const response = await fetch(
        `${API_BASE}/api/metasupply/briefings/${currentBriefing.briefing_id}/sections/${sectionKey}/regenerate`,
        { method: 'POST', credentials: 'include' }
      );
      if (!response.ok) throw new Error(`Regeneration failed (${response.status})`);
      const data = await response.json();
      // Update the section in the current briefing
      setCurrentBriefing(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map(s =>
            s.section_key === sectionKey
              ? { ...s, content: data.content, confidence: data.confidence, data_points: data.data_points || s.data_points }
              : s
          ),
        };
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to regenerate section';
      setGenerationError(message);
    } finally {
      setRegeneratingSection(null);
    }
  };

  // ── API: Download PDF ───────────────────────────────────────
  const handleDownloadPdf = async (briefingId: number) => {
    setDownloadingPdf(true);
    try {
      const response = await fetch(`${API_BASE}/api/metasupply/briefings/${briefingId}/pdf`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`PDF download failed (${response.status})`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      a.download = `briefing-${briefingId}-${timestamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'PDF download failed';
      setGenerationError(message);
    } finally {
      setDownloadingPdf(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleDataPoints = (key: string) => {
    setExpandedDataPoints(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSectionOption = (key: string) => {
    const option = SECTION_OPTIONS.find(s => s.key === key);
    if (option?.required) return; // Cannot uncheck required sections
    setSelectedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getConfidenceColor = (confidence: string): { bg: string; text: string; border: string } => {
    switch (confidence) {
      case 'high': return { bg: T.successBg, text: T.success, border: '#BBF7D0' };
      case 'medium': return { bg: T.warningBg, text: T.warning, border: '#FDE68A' };
      case 'low': return { bg: T.dangerBg, text: T.danger, border: '#FECACA' };
      default: return { bg: '#F1F5F9', text: T.textMid, border: T.border };
    }
  };

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getAudienceBadge = (aud: string) => {
    const label = AUDIENCE_LABELS[aud] || aud;
    const colors: Record<string, { bg: string; text: string }> = {
      c_suite: { bg: '#EFF6FF', text: '#1D4ED8' },
      procurement: { bg: '#F0FDF4', text: '#15803D' },
      compliance: { bg: '#FDF4FF', text: '#7C3AED' },
      board: { bg: '#FFFBEB', text: '#B45309' },
    };
    const c = colors[aud] || { bg: '#F1F5F9', text: T.textMid };
    return (
      <span style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        background: c.bg,
        color: c.text,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}>
        {label}
      </span>
    );
  };

  // Simple markdown renderer for briefing content
  const renderMarkdown = (content: string) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let listItems: string[] = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} style={{ paddingLeft: 20, margin: '8px 0', lineHeight: 1.8 }}>
            {listItems.map((li, idx) => (
              <li key={idx} style={{ fontSize: 14, color: T.textDark, marginBottom: 4 }}>
                {renderInlineMarkdown(li)}
              </li>
            ))}
          </ul>
        );
        listItems = [];
      }
    };

    const renderInlineMarkdown = (text: string): React.ReactNode => {
      // Bold
      const parts = text.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={idx}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
    };

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) {
        flushList();
        return;
      }

      // Headers
      if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(
          <h4 key={idx} style={{ fontSize: 15, fontWeight: 700, color: T.navy, marginTop: 16, marginBottom: 8 }}>
            {trimmed.slice(4)}
          </h4>
        );
        return;
      }
      if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(
          <h3 key={idx} style={{ fontSize: 17, fontWeight: 700, color: T.navy, marginTop: 20, marginBottom: 10 }}>
            {trimmed.slice(3)}
          </h3>
        );
        return;
      }

      // List items
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        listItems.push(trimmed.slice(2));
        return;
      }
      if (/^\d+\.\s/.test(trimmed)) {
        listItems.push(trimmed.replace(/^\d+\.\s/, ''));
        return;
      }

      // Paragraph
      flushList();
      elements.push(
        <p key={idx} style={{ fontSize: 14, color: T.textDark, lineHeight: 1.7, marginBottom: 10 }}>
          {renderInlineMarkdown(trimmed)}
        </p>
      );
    });

    flushList();
    return elements;
  };

  // Templates for the selected audience
  const audienceTemplates = templates.filter(t => t.audience === audience);

  // ============================================================
  // RENDER: Hero / Page Header
  // ============================================================
  const renderHero = () => (
    <div style={{
      position: 'relative',
      background: T.offWhite, border: `1px solid ${T.border}`,
      borderRadius: T.radius,
      padding: isMobile ? '28px 20px' : '40px 44px',
      overflow: 'hidden',
      marginBottom: 24,
    }}>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h1 style={{
          fontSize: isMobile ? 24 : 32,
          fontWeight: 800,
          color: T.textDark,
          letterSpacing: '-0.02em',
          marginBottom: 8,
          fontFamily: "'Playfair Display', Georgia, serif",
        }}>
          Executive Briefing Generator
        </h1>
        <p style={{
          fontSize: isMobile ? 13 : 15,
          color: T.textMid,
          maxWidth: 600,
        }}>
          Generate AI-powered executive briefings tailored to your audience. Analyze portfolio risk, compliance, shortages, and global factors.
        </p>
      </div>
    </div>
  );

  // ============================================================
  // RENDER: Tab Navigation
  // ============================================================
  const renderTabs = () => {
    const tabs: { id: ViewMode; label: string }[] = [
      { id: 'generator', label: 'Generate' },
      { id: 'history', label: 'History' },
      { id: 'templates', label: 'Templates' },
    ];
    if (currentBriefing) {
      tabs.splice(1, 0, { id: 'preview', label: 'Current Briefing' });
    }

    return (
      <div style={{
        display: 'flex',
        gap: 4,
        marginBottom: 24,
        borderBottom: `2px solid ${T.border}`,
        paddingBottom: 0,
        overflowX: 'auto',
      }}>
        {tabs.map(tab => {
          const active = viewMode === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setViewMode(tab.id)}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderBottom: active ? `2px solid ${T.gold}` : '2px solid transparent',
                background: 'transparent',
                color: active ? T.navy : T.textMid,
                fontSize: 14,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.2s',
                marginBottom: -2,
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    );
  };

  // ============================================================
  // RENDER: Audience Selector Cards
  // ============================================================
  const renderAudienceSelector = () => (
    <div style={{ marginBottom: 24 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 700, color: T.textLight,
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12,
      }}>
        Target Audience
      </label>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: 12,
      }}>
        {(Object.keys(AUDIENCE_CONFIG) as AudienceType[]).map(key => {
          const config = AUDIENCE_CONFIG[key];
          const selected = audience === key;
          return (
            <button
              key={key}
              onClick={() => {
                setAudience(key);
                setSelectedTemplateId(null); // Reset template when audience changes
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                padding: '20px 16px',
                borderRadius: T.radius,
                border: `2px solid ${selected ? T.gold : T.border}`,
                background: selected ? `${T.gold}0F` : T.white,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.2s',
                boxShadow: selected ? `0 0 0 1px ${T.gold}` : 'none',
              }}
            >
              <span style={{ color: selected ? T.gold : T.textMid }}>{config.icon}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: selected ? T.navy : T.textDark }}>
                {config.label}
              </span>
              <span style={{ fontSize: 11, color: T.textLight, textAlign: 'center', lineHeight: 1.4 }}>
                {config.description}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ============================================================
  // RENDER: Template Picker
  // ============================================================
  const renderTemplatePicker = () => {
    if (templatesLoading) return null;
    if (audienceTemplates.length === 0) return null;

    return (
      <div style={{ marginBottom: 24 }}>
        <label style={{
          display: 'block', fontSize: 11, fontWeight: 700, color: T.textLight,
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
        }}>
          Template
        </label>
        <select
          value={selectedTemplateId ?? ''}
          onChange={e => setSelectedTemplateId(e.target.value ? Number(e.target.value) : null)}
          style={{
            width: '100%',
            padding: '10px 14px',
            borderRadius: T.radiusSm,
            border: `1px solid ${T.border}`,
            background: T.white,
            color: T.textDark,
            fontSize: 14,
            fontFamily: 'inherit',
            outline: 'none',
          }}
        >
          <option value="">Default template</option>
          {audienceTemplates.map(t => (
            <option key={t.template_id} value={t.template_id}>
              {t.name}{t.is_default ? ' (Default)' : ''}
            </option>
          ))}
        </select>
        {audienceTemplates.find(t => t.template_id === selectedTemplateId)?.description && (
          <p style={{ fontSize: 12, color: T.textLight, marginTop: 6 }}>
            {audienceTemplates.find(t => t.template_id === selectedTemplateId)?.description}
          </p>
        )}
      </div>
    );
  };

  // ============================================================
  // RENDER: Scope Selector
  // ============================================================
  const renderScopeSelector = () => (
    <div style={{ marginBottom: 24 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 700, color: T.textLight,
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12,
      }}>
        Briefing Scope
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {(['full_portfolio', 'therapeutic_class', 'manufacturer'] as ScopeType[]).map(s => (
          <label
            key={s}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              padding: '10px 14px', borderRadius: T.radiusSm,
              border: `1px solid ${scope === s ? T.gold : T.border}`,
              background: scope === s ? `${T.gold}0A` : T.white,
              transition: 'all 0.2s',
            }}
          >
            <input
              type="radio"
              name="scope"
              checked={scope === s}
              onChange={() => { setScope(s); setScopeValue(''); }}
              style={{ accentColor: T.gold }}
            />
            <span style={{ fontSize: 14, fontWeight: scope === s ? 600 : 400, color: T.textDark }}>
              {SCOPE_LABELS[s]}
            </span>
          </label>
        ))}
      </div>
      {(scope === 'therapeutic_class' || scope === 'manufacturer') && (
        <input
          type="text"
          value={scopeValue}
          onChange={e => setScopeValue(e.target.value)}
          placeholder={scope === 'therapeutic_class' ? 'e.g. Antibiotics' : 'e.g. Pfizer'}
          style={{
            width: '100%',
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: T.radiusSm,
            border: `1px solid ${T.border}`,
            background: T.white,
            color: T.textDark,
            fontSize: 14,
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
      )}
    </div>
  );

  // ============================================================
  // RENDER: Time Period Selector
  // ============================================================
  const renderTimePeriodSelector = () => (
    <div style={{ marginBottom: 24 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 700, color: T.textLight,
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12,
      }}>
        Time Period
      </label>
      <div style={{
        display: 'flex',
        borderRadius: T.radiusSm,
        border: `1px solid ${T.border}`,
        overflow: 'hidden',
      }}>
        {(['7d', '30d', '90d', '1y'] as TimePeriod[]).map(tp => {
          const active = timePeriod === tp;
          return (
            <button
              key={tp}
              onClick={() => setTimePeriod(tp)}
              style={{
                flex: 1,
                padding: '10px 0',
                border: 'none',
                background: active ? T.navy : T.white,
                color: active ? T.white : T.textMid,
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.2s',
                borderRight: `1px solid ${T.border}`,
              }}
            >
              {TIME_PERIOD_LABELS[tp]}
            </button>
          );
        })}
      </div>
    </div>
  );

  // ============================================================
  // RENDER: Section Customization (Advanced)
  // ============================================================
  const renderSectionCustomization = () => (
    <div style={{ marginBottom: 24 }}>
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, border: 'none',
          background: 'none', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 13, fontWeight: 600, color: T.textMid,
          padding: 0,
        }}
      >
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2"
          style={{ transform: showAdvanced ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        Customize Sections
      </button>
      {showAdvanced && (
        <div style={{
          marginTop: 12, padding: 16, borderRadius: T.radiusSm,
          border: `1px solid ${T.border}`, background: T.offWhite,
        }}>
          {SECTION_OPTIONS.map(opt => (
            <label
              key={opt.key}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 0', cursor: opt.required ? 'not-allowed' : 'pointer',
                opacity: opt.required && !selectedSections.has(opt.key) ? 0.5 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={selectedSections.has(opt.key)}
                onChange={() => toggleSectionOption(opt.key)}
                disabled={opt.required}
                style={{ accentColor: T.gold }}
              />
              <span style={{ fontSize: 13, color: T.textDark }}>
                {opt.label}
                {opt.required && (
                  <span style={{ fontSize: 10, color: T.textLight, marginLeft: 6 }}>(required)</span>
                )}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  // ============================================================
  // RENDER: Custom Title Input
  // ============================================================
  const renderTitleInput = () => (
    <div style={{ marginBottom: 24 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 700, color: T.textLight,
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
      }}>
        Custom Title <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
      </label>
      <input
        type="text"
        value={customTitle}
        onChange={e => setCustomTitle(e.target.value)}
        placeholder="Auto-generated if blank"
        style={{
          width: '100%',
          padding: '10px 14px',
          borderRadius: T.radiusSm,
          border: `1px solid ${T.border}`,
          background: T.white,
          color: T.textDark,
          fontSize: 14,
          fontFamily: 'inherit',
          outline: 'none',
        }}
      />
    </div>
  );

  // ============================================================
  // RENDER: Generate Button / Progress
  // ============================================================
  const renderGenerateButton = () => (
    <div>
      {generationError && (
        <div style={{
          padding: '12px 16px', marginBottom: 16,
          background: T.dangerBg, border: `1px solid ${T.danger}33`,
          borderRadius: T.radiusSm, color: T.danger, fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <IconAlert />
          {generationError}
        </div>
      )}

      {isGenerating ? (
        <div style={{
          padding: 24, borderRadius: T.radius,
          border: `1px solid ${T.border}`, background: T.white,
          textAlign: 'center',
        }}>
          <div style={{ marginBottom: 16 }}>
            <IconSpinner />
          </div>
          <div style={{
            height: 6, borderRadius: 3, background: T.border,
            overflow: 'hidden', marginBottom: 12,
          }}>
            <div style={{
              height: '100%', borderRadius: 3,
              background: `linear-gradient(90deg, ${T.gold}, ${T.goldLight})`,
              width: `${generationProgress}%`,
              transition: 'width 0.5s ease',
            }} />
          </div>
          <p style={{ fontSize: 13, color: T.textMid, fontStyle: 'italic' }}>
            {generationMessage}
          </p>
        </div>
      ) : (
        <button
          onClick={handleGenerate}
          disabled={scope !== 'full_portfolio' && !scopeValue.trim()}
          style={{
            width: '100%',
            padding: '14px 24px',
            borderRadius: T.radius,
            border: 'none',
            background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
            color: T.white,
            fontSize: 16,
            fontWeight: 700,
            cursor: scope !== 'full_portfolio' && !scopeValue.trim() ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            transition: 'all 0.2s',
            opacity: scope !== 'full_portfolio' && !scopeValue.trim() ? 0.6 : 1,
            boxShadow: `0 2px 8px ${T.gold}4D`,
          }}
        >
          Generate Briefing
        </button>
      )}
    </div>
  );

  // ============================================================
  // RENDER: Generator Form
  // ============================================================
  const renderGenerator = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile || isTablet ? '1fr' : '1fr 1fr',
      gap: 24,
    }}>
      {/* Left: Form */}
      <div style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: isMobile ? 20 : 28,
        boxShadow: T.shadow,
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: T.navy, marginBottom: 24 }}>
          Configure Briefing
        </h3>
        {renderAudienceSelector()}
        {renderTemplatePicker()}
        {renderScopeSelector()}
        {renderTimePeriodSelector()}
        {renderTitleInput()}
        {renderSectionCustomization()}
        {renderGenerateButton()}
      </div>

      {/* Right: Quick summary / recent briefings */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}>
        {/* Info card */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: isMobile ? 20 : 28,
          boxShadow: T.shadow,
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 12 }}>
            About This Briefing
          </h3>
          <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.7 }}>
            <p style={{ marginBottom: 10 }}>
              <strong>Audience:</strong> {AUDIENCE_CONFIG[audience].label} — {AUDIENCE_CONFIG[audience].description}
            </p>
            <p style={{ marginBottom: 10 }}>
              <strong>Scope:</strong> {SCOPE_LABELS[scope]}
              {scopeValue && ` — ${scopeValue}`}
            </p>
            <p style={{ marginBottom: 10 }}>
              <strong>Time Period:</strong> {TIME_PERIOD_LABELS[timePeriod]}
            </p>
            <p style={{ marginBottom: 10 }}>
              <strong>Sections:</strong> {selectedSections.size} of {SECTION_OPTIONS.length} included
            </p>
          </div>
          <div style={{
            marginTop: 16, padding: 12, borderRadius: T.radiusSm,
            background: T.infoBg, fontSize: 12, color: '#1D4ED8', lineHeight: 1.6,
          }}>
            Briefings are generated using AI analysis of your portfolio data. Generation typically takes 15–30 seconds.
          </div>
        </div>

        {/* Recent briefings quick list */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: isMobile ? 20 : 28,
          boxShadow: T.shadow,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy }}>
              Recent Briefings
            </h3>
            <button
              onClick={() => { setViewMode('history'); loadBriefings(); }}
              style={{
                border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, color: T.gold, fontFamily: 'inherit',
              }}
            >
              View All
            </button>
          </div>
          <RecentBriefingsList
            onView={loadBriefingDetail}
            getAudienceBadge={getAudienceBadge}
            formatDate={formatDate}
          />
        </div>
      </div>
    </div>
  );

  // ============================================================
  // RENDER: Briefing Preview
  // ============================================================
  const renderPreview = () => {
    if (!currentBriefing) {
      return (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <p style={{ color: T.textLight }}>No briefing loaded. Generate one first.</p>
          <button
            onClick={() => setViewMode('generator')}
            style={{
              marginTop: 16, padding: '10px 24px', borderRadius: T.radiusSm,
              border: `1px solid ${T.border}`, background: T.white,
              color: T.navy, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Go to Generator
          </button>
        </div>
      );
    }

    const b = currentBriefing;

    return (
      <div>
        {/* Header */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: isMobile ? 20 : 28,
          boxShadow: T.shadow,
          marginBottom: 20,
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div>
              <h2 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: T.navy, marginBottom: 8 }}>
                {b.title}
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, fontSize: 13, color: T.textMid }}>
                {getAudienceBadge(b.audience)}
                <span>{SCOPE_LABELS[b.scope] || b.scope}</span>
                <span>•</span>
                <span>{TIME_PERIOD_LABELS[b.time_period] || b.time_period}</span>
                <span>•</span>
                <span>{formatDate(b.generated_at)}</span>
              </div>
              {b.token_usage && (
                <p style={{ fontSize: 11, color: T.textLight, marginTop: 6 }}>
                  Tokens: {b.token_usage.input_tokens?.toLocaleString()} in / {b.token_usage.output_tokens?.toLocaleString()} out
                  {b.token_usage.cost_estimate && ` • Est. cost: ${b.token_usage.cost_estimate}`}
                </p>
              )}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button
                onClick={() => handleDownloadPdf(b.briefing_id)}
                disabled={downloadingPdf}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: T.radiusSm,
                  border: `1px solid ${T.gold}`,
                  background: downloadingPdf ? T.offWhite : T.white,
                  color: T.gold, fontSize: 13, fontWeight: 600,
                  cursor: downloadingPdf ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', transition: 'all 0.2s',
                }}
              >
                {downloadingPdf ? (
                  <IconSpinner />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                )}
                Download PDF
              </button>
              <button
                onClick={() => handleDuplicateBriefing(b.briefing_id)}
                disabled={duplicatingId === b.briefing_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: T.radiusSm,
                  border: `1px solid ${T.border}`,
                  background: T.white, color: T.textMid,
                  fontSize: 13, fontWeight: 600,
                  cursor: duplicatingId === b.briefing_id ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', transition: 'all 0.2s',
                }}
              >
                {duplicatingId === b.briefing_id ? (
                  <IconSpinner />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" /><rect x="2" y="2" width="13" height="13" rx="2" />
                  </svg>
                )}
                Duplicate
              </button>
              <button
                onClick={() => handleRerunBriefing(b.briefing_id)}
                disabled={rerunningId === b.briefing_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: T.radiusSm,
                  border: `1px solid ${T.border}`,
                  background: T.white, color: T.navyLight,
                  fontSize: 13, fontWeight: 600,
                  cursor: rerunningId === b.briefing_id ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', transition: 'all 0.2s',
                }}
              >
                {rerunningId === b.briefing_id ? (
                  <IconSpinner />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                    <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                  </svg>
                )}
                Re-run
              </button>
              <button
                onClick={() => { setDeleteConfirmId(b.briefing_id); setDeleteSource('detail'); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: T.radiusSm,
                  border: `1px solid ${T.danger}`,
                  background: T.white, color: T.danger,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit', transition: 'all 0.2s',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Delete
              </button>
              <button
                onClick={() => setViewMode('generator')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: T.radiusSm,
                  border: 'none',
                  background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
                  color: T.white,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit', transition: 'all 0.2s',
                  boxShadow: `0 2px 6px ${T.gold}40`,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                New Briefing
              </button>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: 16,
          marginBottom: 20,
          boxShadow: T.shadow,
        }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
            Table of Contents
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {b.sections.map((section, idx) => {
              const confColor = getConfidenceColor(section.confidence);
              return (
                <button
                  key={section.section_key}
                  onClick={() => {
                    const el = document.getElementById(`section-${section.section_key}`);
                    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px', borderRadius: 20,
                    border: `1px solid ${T.border}`,
                    background: T.offWhite, color: T.textDark,
                    fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    fontFamily: 'inherit', transition: 'all 0.15s',
                  }}
                >
                  <span style={{ fontSize: 11, color: T.textLight }}>{idx + 1}.</span>
                  {section.section_title}
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: confColor.text,
                    flexShrink: 0,
                  }} title={`Confidence: ${section.confidence}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Sections */}
        {b.sections.map(section => {
          const isExpanded = expandedSections.has(section.section_key);
          const showData = expandedDataPoints.has(section.section_key);
          const confColor = getConfidenceColor(section.confidence);
          const isRegenerating = regeneratingSection === section.section_key;

          return (
            <div
              key={section.section_key}
              id={`section-${section.section_key}`}
              style={{
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: T.radius,
                marginBottom: 16,
                boxShadow: T.shadow,
                overflow: 'hidden',
              }}
            >
              {/* Section header */}
              <button
                onClick={() => toggleSection(section.section_key)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '16px 20px',
                  border: 'none', background: isExpanded ? T.offWhite : T.white,
                  cursor: 'pointer', fontFamily: 'inherit',
                  transition: 'background 0.2s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, margin: 0 }}>
                    {section.section_title}
                  </h3>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10,
                    fontSize: 10, fontWeight: 700,
                    background: confColor.bg, color: confColor.text,
                    border: `1px solid ${confColor.border}`,
                    textTransform: 'uppercase',
                  }}>
                    {section.confidence}
                  </span>
                </div>
                <svg
                  width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke={T.textLight} strokeWidth="2"
                  style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>

              {/* Section content */}
              {isExpanded && (
                <div style={{ padding: '0 20px 20px' }}>
                  {isRegenerating ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <IconSpinner />
                      <p style={{ fontSize: 13, color: T.textMid, marginTop: 12 }}>Regenerating section...</p>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: 16 }}>
                        {renderMarkdown(section.content)}
                      </div>

                      {/* Actions row */}
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 8,
                        paddingTop: 12, borderTop: `1px solid ${T.borderLight}`,
                      }}>
                        <button
                          onClick={() => handleRegenerateSection(section.section_key)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '6px 12px', borderRadius: T.radiusSm,
                            border: `1px solid ${T.border}`,
                            background: T.white, color: T.textMid,
                            fontSize: 12, fontWeight: 500, cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                            <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                          </svg>
                          Regenerate
                        </button>
                        {section.data_points && section.data_points.length > 0 && (
                          <button
                            onClick={() => toggleDataPoints(section.section_key)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '6px 12px', borderRadius: T.radiusSm,
                              border: `1px solid ${T.border}`,
                              background: showData ? T.offWhite : T.white,
                              color: T.textMid, fontSize: 12, fontWeight: 500,
                              cursor: 'pointer', fontFamily: 'inherit',
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <ellipse cx="12" cy="5" rx="9" ry="3" />
                              <path d="M3 5V19A9 3 0 0 0 21 19V5" />
                              <path d="M3 12A9 3 0 0 0 21 12" />
                            </svg>
                            {showData ? 'Hide' : 'Show'} Data Points
                          </button>
                        )}
                      </div>

                      {/* Data points panel */}
                      {showData && section.data_points && section.data_points.length > 0 && (
                        <div style={{
                          marginTop: 12, padding: 14, borderRadius: T.radiusSm,
                          background: T.offWhite, border: `1px solid ${T.borderLight}`,
                          maxHeight: 300, overflowY: 'auto',
                        }}>
                          <h5 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                            Source Data Points
                          </h5>
                          <pre style={{
                            fontSize: 11, color: T.textMid,
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                            margin: 0, fontFamily: 'monospace',
                          }}>
                            {JSON.stringify(section.data_points, null, 2)}
                          </pre>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ============================================================
  // RENDER: Briefing History (Enhanced — Issue #206)
  // ============================================================

  const actionBtnStyle = (color: string, borderColor?: string): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '4px 10px', borderRadius: 6,
    border: `1px solid ${borderColor || T.border}`, background: T.white,
    cursor: 'pointer', color, fontSize: 12, fontWeight: 600,
    fontFamily: 'inherit', transition: 'all 0.15s', whiteSpace: 'nowrap',
  });

  const renderHistoryCard = (item: BriefingListItem) => {
    const isSelected = selectedBriefings.has(item.briefing_id);
    return (
      <div
        key={item.briefing_id}
        style={{
          background: T.white,
          border: `1px solid ${isSelected ? T.gold : T.border}`,
          borderRadius: T.radius,
          padding: isMobile ? 16 : 20,
          boxShadow: isSelected ? `0 0 0 1px ${T.gold}` : T.shadow,
          transition: 'all 0.2s',
        }}
      >
        {/* Card header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleBriefingSelection(item.briefing_id)}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: T.gold, flexShrink: 0 }}
              />
              <button
                onClick={() => loadBriefingDetail(item.briefing_id)}
                style={{
                  border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: 15, fontWeight: 700, color: T.navy,
                  fontFamily: 'inherit', textAlign: 'left', padding: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {item.title}
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginLeft: 24 }}>
              {getAudienceBadge(item.audience)}
              <span style={{ fontSize: 12, color: T.textLight }}>
                {SCOPE_LABELS[item.scope] || item.scope}
              </span>
              <span style={{ fontSize: 12, color: T.textLight }}>•</span>
              <span style={{ fontSize: 12, color: T.textLight }}>
                {item.section_count} section{item.section_count !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Card footer */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
          paddingTop: 12, borderTop: `1px solid ${T.borderLight}`, gap: 8,
        }}>
          <div style={{ fontSize: 12, color: T.textLight }}>
            {formatDate(item.generated_at)} • by {item.generated_by_name}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button onClick={() => loadBriefingDetail(item.briefing_id)} title="View" style={actionBtnStyle(T.navy)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              View
            </button>
            <button onClick={() => handleDownloadPdf(item.briefing_id)} title="PDF" style={actionBtnStyle(T.gold, T.gold)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              PDF
            </button>
            <button
              onClick={() => handleRerunBriefing(item.briefing_id)}
              disabled={rerunningId === item.briefing_id}
              title="Re-run with latest data"
              style={actionBtnStyle(T.navyLight)}
            >
              {rerunningId === item.briefing_id ? <IconSpinner /> : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /></svg>
              )}
              Re-run
            </button>
            <button
              onClick={() => handleDuplicateBriefing(item.briefing_id)}
              disabled={duplicatingId === item.briefing_id}
              title="Duplicate"
              style={actionBtnStyle(T.textMid)}
            >
              {duplicatingId === item.briefing_id ? <IconSpinner /> : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><rect x="2" y="2" width="13" height="13" rx="2" /></svg>
              )}
              Duplicate
            </button>
            <button onClick={() => { setDeleteConfirmId(item.briefing_id); setDeleteSource('history'); }} title="Delete" style={actionBtnStyle(T.danger)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderHistory = () => (
    <div>
      {/* Top action bar: Generate New + Bulk export */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, marginBottom: 16,
      }}>
        <button
          onClick={() => setViewMode('generator')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 24px', borderRadius: T.radiusSm,
            border: 'none',
            background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
            color: T.white, fontSize: 14, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: `0 2px 8px ${T.gold}4D`,
            transition: 'all 0.2s',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Generate New
        </button>
        {selectedBriefings.size > 0 && (
          <button
            onClick={handleBulkExport}
            disabled={bulkExporting}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 20px', borderRadius: T.radiusSm,
              border: `1px solid ${T.gold}`,
              background: T.white, color: T.gold,
              fontSize: 13, fontWeight: 600,
              cursor: bulkExporting ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', transition: 'all 0.2s',
            }}
          >
            {bulkExporting ? <IconSpinner /> : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
            Export {selectedBriefings.size} as PDF{selectedBriefings.size > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Filters + View toggle + Sort */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20,
        alignItems: 'center',
      }}>
        <input
          type="text"
          placeholder="Search briefings..."
          value={historySearch}
          onChange={e => { setHistorySearch(e.target.value); setBriefingsPage(1); }}
          style={{
            flex: 1, minWidth: 180,
            padding: '10px 14px', borderRadius: T.radiusSm,
            border: `1px solid ${T.border}`, background: T.white,
            color: T.textDark, fontSize: 14, fontFamily: 'inherit',
            outline: 'none',
          }}
        />
        <select
          value={historyAudienceFilter}
          onChange={e => { setHistoryAudienceFilter(e.target.value); setBriefingsPage(1); }}
          style={{
            padding: '10px 14px', borderRadius: T.radiusSm,
            border: `1px solid ${T.border}`, background: T.white,
            color: T.textDark, fontSize: 14, fontFamily: 'inherit',
            outline: 'none',
          }}
        >
          <option value="">All Audiences</option>
          <option value="c_suite">C-Suite</option>
          <option value="procurement">Procurement</option>
          <option value="compliance">Compliance</option>
          <option value="board">Board</option>
        </select>

        {/* Sort toggle */}
        <button
          onClick={() => { setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc'); setBriefingsPage(1); }}
          title={`Sort: ${sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}`}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '10px 14px', borderRadius: T.radiusSm,
            border: `1px solid ${T.border}`, background: T.white,
            color: T.textMid, fontSize: 13, fontWeight: 500,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {sortOrder === 'desc' ? (
              <><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></>
            ) : (
              <><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></>
            )}
          </svg>
          {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
        </button>

        {/* View toggle (table/card) */}
        <div style={{ display: 'flex', borderRadius: T.radiusSm, overflow: 'hidden', border: `1px solid ${T.border}` }}>
          <button
            onClick={() => setHistoryViewStyle('table')}
            title="Table view"
            style={{
              padding: '9px 12px', border: 'none',
              background: historyViewStyle === 'table' ? T.navy : T.white,
              color: historyViewStyle === 'table' ? T.white : T.textMid,
              cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" />
            </svg>
          </button>
          <button
            onClick={() => setHistoryViewStyle('card')}
            title="Card view"
            style={{
              padding: '9px 12px', border: 'none', borderLeft: `1px solid ${T.border}`,
              background: historyViewStyle === 'card' ? T.navy : T.white,
              color: historyViewStyle === 'card' ? T.white : T.textMid,
              cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="9" height="8" rx="1" /><rect x="13" y="3" width="9" height="8" rx="1" /><rect x="2" y="13" width="9" height="8" rx="1" /><rect x="13" y="13" width="9" height="8" rx="1" />
            </svg>
          </button>
        </div>
      </div>

      {briefingsError && (
        <div style={{
          padding: '12px 16px', marginBottom: 16,
          background: T.dangerBg, border: `1px solid ${T.danger}33`,
          borderRadius: T.radiusSm, color: T.danger, fontSize: 13,
        }}>
          {briefingsError}
        </div>
      )}

      {briefingsLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <IconSpinner />
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 12 }}>Loading briefings...</p>
        </div>
      ) : briefingsList.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 60,
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: T.radius,
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="1.5" style={{ marginBottom: 16 }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy }}>No Briefings Yet</h3>
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>
            Generate your first executive briefing to see it here.
          </p>
          <button
            onClick={() => setViewMode('generator')}
            style={{
              marginTop: 16, padding: '10px 24px', borderRadius: T.radiusSm,
              border: 'none', background: T.gold, color: T.white,
              fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Generate Briefing
          </button>
        </div>
      ) : (
        <>
          {/* Select all row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
            fontSize: 13, color: T.textMid,
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedBriefings.size === briefingsList.length && briefingsList.length > 0}
                onChange={toggleSelectAll}
                style={{ width: 15, height: 15, cursor: 'pointer', accentColor: T.gold }}
              />
              Select all ({briefingsList.length})
            </label>
            {selectedBriefings.size > 0 && (
              <span style={{ color: T.gold, fontWeight: 600 }}>
                {selectedBriefings.size} selected
              </span>
            )}
          </div>

          {/* Card view */}
          {historyViewStyle === 'card' ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : 'repeat(2, 1fr)',
              gap: 16,
            }}>
              {briefingsList.map(item => renderHistoryCard(item))}
            </div>
          ) : (
            /* Table view */
            <div style={{
              background: T.white, border: `1px solid ${T.border}`,
              borderRadius: T.radius, overflow: 'hidden',
              boxShadow: T.shadow,
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                      <th style={{ padding: '12px 12px', width: 32 }}>
                        <input
                          type="checkbox"
                          checked={selectedBriefings.size === briefingsList.length && briefingsList.length > 0}
                          onChange={toggleSelectAll}
                          style={{ width: 15, height: 15, cursor: 'pointer', accentColor: T.gold }}
                        />
                      </th>
                      {['Title', 'Audience', 'Date Generated', 'Generated By', 'Actions'].map(col => (
                        <th key={col} style={{
                          padding: '12px 16px', textAlign: 'left',
                          fontSize: 11, fontWeight: 700, color: T.textLight,
                          textTransform: 'uppercase', letterSpacing: '0.06em',
                          whiteSpace: 'nowrap',
                        }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {briefingsList.map(item => {
                      const isSelected = selectedBriefings.has(item.briefing_id);
                      return (
                        <tr
                          key={item.briefing_id}
                          style={{
                            borderBottom: `1px solid ${T.borderLight}`,
                            background: isSelected ? `${T.gold}0A` : 'transparent',
                          }}
                        >
                          <td style={{ padding: '12px 12px' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleBriefingSelection(item.briefing_id)}
                              style={{ width: 15, height: 15, cursor: 'pointer', accentColor: T.gold }}
                            />
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <button
                              onClick={() => loadBriefingDetail(item.briefing_id)}
                              style={{
                                border: 'none', background: 'none', cursor: 'pointer',
                                fontSize: 14, fontWeight: 600, color: T.navy,
                                fontFamily: 'inherit', textAlign: 'left', padding: 0,
                              }}
                            >
                              {item.title}
                            </button>
                            <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                              {SCOPE_LABELS[item.scope] || item.scope} • {item.section_count} sections
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>{getAudienceBadge(item.audience)}</td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: T.textMid, whiteSpace: 'nowrap' }}>
                            {formatDate(item.generated_at)}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: T.textMid }}>
                            {item.generated_by_name}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                              <button onClick={() => loadBriefingDetail(item.briefing_id)} title="View" style={actionBtnStyle(T.navy)}>
                                View
                              </button>
                              <button onClick={() => handleDownloadPdf(item.briefing_id)} title="PDF" style={actionBtnStyle(T.gold, T.gold)}>
                                PDF
                              </button>
                              <button
                                onClick={() => handleRerunBriefing(item.briefing_id)}
                                disabled={rerunningId === item.briefing_id}
                                title="Re-run"
                                style={actionBtnStyle(T.navyLight)}
                              >
                                {rerunningId === item.briefing_id ? '...' : 'Re-run'}
                              </button>
                              <button
                                onClick={() => handleDuplicateBriefing(item.briefing_id)}
                                disabled={duplicatingId === item.briefing_id}
                                title="Duplicate"
                                style={actionBtnStyle(T.textMid)}
                              >
                                {duplicatingId === item.briefing_id ? '...' : 'Dup'}
                              </button>
                              <button
                                onClick={() => { setDeleteConfirmId(item.briefing_id); setDeleteSource('history'); }}
                                title="Delete"
                                style={actionBtnStyle(T.danger)}
                              >
                                Del
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {briefingsTotal > 25 && (
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              gap: 12, marginTop: 20,
            }}>
              <button
                disabled={briefingsPage <= 1}
                onClick={() => setBriefingsPage(p => p - 1)}
                style={{
                  padding: '8px 16px', borderRadius: T.radiusSm,
                  border: `1px solid ${T.border}`, background: T.white,
                  color: briefingsPage <= 1 ? T.textLight : T.textDark,
                  cursor: briefingsPage <= 1 ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontFamily: 'inherit',
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: 13, color: T.textMid }}>
                Page {briefingsPage} of {Math.ceil(briefingsTotal / 25)}
              </span>
              <button
                disabled={briefingsPage * 25 >= briefingsTotal}
                onClick={() => setBriefingsPage(p => p + 1)}
                style={{
                  padding: '8px 16px', borderRadius: T.radiusSm,
                  border: `1px solid ${T.border}`, background: T.white,
                  color: briefingsPage * 25 >= briefingsTotal ? T.textLight : T.textDark,
                  cursor: briefingsPage * 25 >= briefingsTotal ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontFamily: 'inherit',
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  // ============================================================
  // RENDER: Templates View
  // ============================================================
  const renderTemplates = () => (
    <div>
      {templatesLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <IconSpinner />
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 12 }}>Loading templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 60,
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: T.radius,
        }}>
          <IconShield />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginTop: 16 }}>No Templates Available</h3>
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>
            Default templates will be created automatically by the system.
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(2, 1fr)',
          gap: 16,
        }}>
          {templates.map(template => (
            <div
              key={template.template_id}
              style={{
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: T.radius,
                padding: 20,
                boxShadow: T.shadow,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 4 }}>
                    {template.name}
                  </h3>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {getAudienceBadge(template.audience)}
                    {template.is_default && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: T.textLight,
                        padding: '2px 6px', borderRadius: 4,
                        background: T.offWhite, border: `1px solid ${T.border}`,
                      }}>
                        Default
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <p style={{ fontSize: 13, color: T.textMid, lineHeight: 1.5, marginBottom: 12 }}>
                {template.description}
              </p>
              <div style={{ fontSize: 12, color: T.textLight }}>
                {template.sections.length} section{template.sections.length !== 1 ? 's' : ''}:
                <span style={{ color: T.textMid, marginLeft: 4 }}>
                  {template.sections.map(s => s.section_title).join(', ')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ============================================================
  // RENDER: Delete Confirmation Modal
  // ============================================================
  const renderDeleteModal = () => {
    if (deleteConfirmId === null) return null;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{
          background: T.white, borderRadius: T.radius,
          padding: 28, maxWidth: 400, width: '100%',
          boxShadow: T.shadowLg,
        }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: T.navy, marginBottom: 12 }}>
            Delete Briefing?
          </h3>
          <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.5, marginBottom: 24 }}>
            This action cannot be undone. The briefing and all its sections will be permanently deleted.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setDeleteConfirmId(null)}
              style={{
                padding: '10px 20px', borderRadius: T.radiusSm,
                border: `1px solid ${T.border}`, background: T.white,
                color: T.textMid, fontSize: 14, fontWeight: 500,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => handleDeleteBriefing(deleteConfirmId)}
              style={{
                padding: '10px 20px', borderRadius: T.radiusSm,
                border: 'none', background: T.danger,
                color: T.white, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {renderHero()}
      {renderTabs()}
      {viewMode === 'generator' && renderGenerator()}
      {viewMode === 'preview' && renderPreview()}
      {viewMode === 'history' && renderHistory()}
      {viewMode === 'templates' && renderTemplates()}
      {renderDeleteModal()}
    </div>
  );
}

// ============================================================
// Sub-component: Recent Briefings List (for generator sidebar)
// ============================================================
function RecentBriefingsList({
  onView,
  getAudienceBadge,
  formatDate,
}: {
  onView: (id: number) => void;
  getAudienceBadge: (aud: string) => React.ReactNode;
  formatDate: (d: string) => string;
}) {
  const { T } = useTheme();
  const [items, setItems] = useState<BriefingListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/metasupply/briefings?page=1&page_size=5&sort_by=generated_at&sort_order=desc`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setItems(data.briefings || []);
        }
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <p style={{ fontSize: 13, color: T.textLight, textAlign: 'center', padding: 20 }}>Loading...</p>;
  }

  if (items.length === 0) {
    return (
      <p style={{ fontSize: 13, color: T.textLight, textAlign: 'center', padding: 20 }}>
        No briefings generated yet.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map(item => (
        <button
          key={item.briefing_id}
          onClick={() => onView(item.briefing_id)}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 12px', borderRadius: T.radiusSm,
            border: `1px solid ${T.borderLight}`,
            background: T.white,
            cursor: 'pointer', fontFamily: 'inherit',
            textAlign: 'left', transition: 'background 0.15s',
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.navy, marginBottom: 2 }}>
              {item.title.length > 40 ? item.title.slice(0, 40) + '...' : item.title}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, color: T.textLight }}>
              {getAudienceBadge(item.audience)}
              <span>{formatDate(item.generated_at)}</span>
            </div>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      ))}
    </div>
  );
}
